import { useAtomValue } from 'jotai'
import Image from 'next/image'
import type {
  AggregatedWalletOption,
  WalletOption,
} from '@/src/hooks/wallet/use-wallet-options'
import { walletAccountsAtom } from '@/src/stores/accounts/wallets'
import { ButtonDiv } from '../ui/button'
import {
  ListRowButton,
  ListRowSide,
  ListRowSideDescription,
  ListRowSideName,
} from '../ui/list-row'
import { AnimatedWalletLogo } from './animated-wallet-logo'

interface WalletListRowProps extends React.HTMLAttributes<HTMLDivElement> {
  wallet: AggregatedWalletOption | WalletOption
  onSelectWallet?: (wallet: AggregatedWalletOption) => void
}

export const WalletListRow = ({
  wallet,
  onSelectWallet,
  className,
}: WalletListRowProps) => {
  const isConnectable = 'connectors' in wallet
  const { groupedAccounts } = useAtomValue(walletAccountsAtom)
  // # of separate accounts for this wallet (not ecosystem/chain accounts)
  const accounts = groupedAccounts.filter(
    (group) => group.provider_info.name === wallet.displayName,
  )

  if (isConnectable) {
    return (
      <ListRowButton
        className={className}
        key={wallet.id}
        onClick={() => onSelectWallet?.(wallet)}
        size='lg'
      >
        <div className='flex items-center gap-4'>
          {wallet.logoUrl && (
            <AnimatedWalletLogo
              logoUrl={wallet.logoUrl}
              displayName={wallet.displayName}
              size={48}
            />
          )}
          <ListRowSide>
            <ListRowSideName>{wallet.displayName}</ListRowSideName>
            {accounts.length > 0 && (
              <ListRowSideDescription>
                {accounts.length} account{accounts.length > 1 ? 's' : ''}
              </ListRowSideDescription>
            )}
          </ListRowSide>
        </div>
        {accounts.length > 0 && (
          <ListRowSide>
            <ButtonDiv variant='secondary' size='sm'>
              Add account
            </ButtonDiv>
          </ListRowSide>
        )}
      </ListRowButton>
    )
  }

  // Installable wallet
  return (
    <ListRowButton
      className={className}
      key={wallet.id}
      onClick={() => window.open(wallet.website, '_blank')}
      size='lg'
    >
      <div className='flex items-center gap-4'>
        {wallet.logoUrl && (
          <Image
            src={wallet.logoUrl}
            alt={`${wallet.displayName} logo`}
            width={48}
            height={48}
            unoptimized
          />
        )}
        <ListRowSide>
          <ListRowSideName>{wallet.displayName}</ListRowSideName>
        </ListRowSide>
      </div>
    </ListRowButton>
  )
}
